import { Formik } from 'formik';
import { GraphQLFormattedError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { useSearchParam } from 'react-use';
import { Box } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import { useWorkspaceLocale } from '@treams/treams-hooks/useWorkspaceLocale';
import * as graphql from '@treams/treams-network';
import {
	ArrowRightIcon,
	Button,
	GraphQLErrorMessage,
	Input,
	Label,
	Paragraph,
	Portal,
	UnderlineLink,
} from '@treams/treams-ui';

import BareLayout from '../../../../../../src/packages/kakapo-ui-application/layouts/BareLayout';
import PublicForm from '../../components/authentication/PublicForm';
import { isMcpAuthorizePath, mcpAuthorizeContinueUrl, mcpAuthorizeErrorKey } from './mcp-authorize';

function apiOrigin(): string {
	const endpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT;
	if (!endpoint) {
		return 'https://api.ontreams.com';
	}
	return new URL(endpoint).origin;
}

function mcpAuthorizeError(key: string): GraphQLFormattedError {
	return {
		message: key,
		extensions: {
			error: { key, data: { details: [] } },
		},
	};
}

export default function Prelogin() {
	const { t } = useTranslation();
	useWorkspaceLocale();

	const [error, attempt] = useErrorBoundary();

	const [prelogin] = graphql.usePreloginMutation();
	const redirect = useSearchParam('redirect');
	const mcpAuthorize = isMcpAuthorizePath(window.location.pathname);
	const loginHint = useSearchParam('login_hint');
	const authorizeError = useSearchParam('error');
	const mcpErrorKey = mcpAuthorize ? mcpAuthorizeErrorKey(authorizeError) : undefined;

	async function onSubmit(variables: { login: string }) {
		if (mcpAuthorize) {
			window.location.href = mcpAuthorizeContinueUrl(
				apiOrigin(),
				window.location.search,
				variables.login,
			);
			return;
		}

		const response = await prelogin({
			variables: {
				input: {
					login: variables.login,
				},
			},
		});

		window.location.href =
			response.data!.prelogin.url + (redirect ? '&redirect=' + encodeURIComponent(redirect) : '');
	}

	return (
		<Portal.Provider>
			<BareLayout>
				<Formik
					initialValues={{ login: mcpAuthorize ? (loginHint ?? '') : '' }}
					onSubmit={attempt(onSubmit)}
				>
					{({ isSubmitting, values, submitForm }) => (
						<PublicForm>
							<GraphQLErrorMessage error={mcpErrorKey ? mcpAuthorizeError(mcpErrorKey) : error} />

							<Box mb={3}>
								<Label mb={2}>{t('common.email')}</Label>
								<Input.Formik
									name="login"
									autoFocus
									inputProps={{ 'aria-label': t('common.email') }}
								/>
							</Box>

							<Button
								mb={3}
								title={t('common.next')}
								rightIcon={<ArrowRightIcon />}
								disabled={values.login === '' || isSubmitting}
								onClick={submitForm}
							/>

							<Paragraph textAlign="center">
								{t('prelogin.noAccount.description')}{' '}
								<UnderlineLink href="https://try.ontreams.com">
									{t('prelogin.noAccount.buttonTitle')}
								</UnderlineLink>
							</Paragraph>
						</PublicForm>
					)}
				</Formik>
			</BareLayout>
		</Portal.Provider>
	);
}
